import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  LoadingSpinner,
  type DataTableColumn,
} from '@atrvd/saas-ui'
import { AlertTriangle, Building2, Plus } from 'lucide-react'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { ReadOnlyActionMenu, ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { DeleteManualBankDialog } from './DeleteManualBankDialog'
import { ManualBankFormDialog, type ManualBankFormMode } from './ManualBankFormDialog'
import { SortHeader } from './SortHeader'
import { MANUAL_BANK_COPY, accountsCountLabel } from './manualBankCopy'
import { signed, twoWaySort, type TableSort } from './manualBankSort'
import { compareText, manualBankPath, type ManualBankInScope } from './manualBanks.utils'
import { useManualBanksInScope } from './useManualBanksInScope'

const DEFAULT_SORT: TableSort[] = [{ columnKey: 'displayName', direction: 'asc' }]

interface ManualBanksSectionProps {
  /** The facility the page is scoped to; null on the organization-wide page. */
  scope: FacilityFinancialAccountsScope | null
}

/**
 * Manual Financial Accounts — the list of manual banks on Financial Accounts
 * settings, mirroring the connections list above it.
 *
 * One row per bank (accounts sharing an institution name); a row opens the
 * bank's detail page. Sorting is client-side: the whole list is in memory,
 * derived from the same account list the rest of the page uses.
 *
 * On a facility's page only the banks with an account of that facility are
 * listed and counted (see `manualBanksInScope`), new banks start at that
 * facility, and an inactive facility's list is read-only.
 */
export function ManualBanksSection({ scope }: ManualBanksSectionProps) {
  const navigate = useNavigate()
  const basePath = financialAccountsBasePath(scope)
  const readOnly = scope?.readOnly ?? false
  const { banks, isLoading, error } = useManualBanksInScope(scope)

  // Local state, so leaving the page and coming back restores the default sort.
  const [sort, setSort] = useState<TableSort[]>(DEFAULT_SORT)
  const [formMode, setFormMode] = useState<ManualBankFormMode | null>(null)
  /** Detail path of the bank awaiting delete confirmation — a bank's unique key. */
  const [deletePath, setDeletePath] = useState<string | null>(null)
  const deleteTarget = banks.find((bank) => manualBankPath(basePath, bank) === deletePath) ?? null

  const columns: DataTableColumn<ManualBankInScope>[] = useMemo(
    () => [
      {
        key: 'displayName',
        header: <SortHeader label="Institution" columnKey="displayName" sort={sort} />,
        headerLabel: 'Institution',
        sortable: true,
        disableHide: true,
        cellClassName: 'font-semibold',
        sortFn: (a, b, direction) => signed(direction, compareText(a.displayName, b.displayName)),
        // A real button so the drill-down is reachable by keyboard (rows are
        // not focusable); it stops propagation so the row click does not
        // navigate a second time.
        render: (bank) => (
          <button
            type="button"
            className="w-full truncate text-left"
            onClick={(e) => {
              e.stopPropagation()
              navigate(manualBankPath(basePath, bank))
            }}
          >
            {bank.displayName}
          </button>
        ),
      },
      {
        key: 'accounts',
        header: <SortHeader label="Accounts & cards" columnKey="accounts" sort={sort} />,
        headerLabel: 'Accounts & cards',
        sortable: true,
        // Equal counts fall back to the name, ascending in either direction.
        sortFn: (a, b, direction) =>
          signed(direction, a.accounts.length - b.accounts.length) ||
          compareText(a.displayName, b.displayName),
        render: (bank) => (
          <span className="text-sm text-slate-700">{accountsCountLabel(bank.accounts.length)}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        headerLabel: 'Actions',
        align: 'right',
        width: '80px',
        disableHide: true,
        render: (bank) => {
          const name = bank.institutionName
          return (
            // Menu clicks bubble through the React tree to the row even though
            // the menu is portaled, so they are stopped here to keep the row
            // from navigating. DataTable ignores `align`, so the cell aligns itself.
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <ReadOnlyActionMenu
                readOnly={readOnly}
                ariaLabel={`Actions for ${bank.displayName}`}
                items={[
                  // The unnamed group has no name to edit.
                  ...(name !== null
                    ? [
                        {
                          id: 'edit',
                          label: MANUAL_BANK_COPY.editBankCta,
                          onClick: () => setFormMode({ kind: 'edit', institutionName: name }),
                        },
                      ]
                    : []),
                  {
                    id: 'delete',
                    label: MANUAL_BANK_COPY.deleteBankCta,
                    variant: 'danger' as const,
                    onClick: () => setDeletePath(manualBankPath(basePath, bank)),
                  },
                ]}
              />
            </div>
          )
        },
      },
    ],
    [navigate, sort, basePath, readOnly],
  )

  const columnPrefs = useTableColumnPrefs('manual-banks', columns)

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <Building2 aria-hidden="true" className="h-5 w-5 shrink-0" />
              <h2 className="text-[22px] font-extrabold tracking-tight">{MANUAL_BANK_COPY.sectionTitle}</h2>
            </div>
            <p className="mt-1.5 text-[15px] text-slate-400">{MANUAL_BANK_COPY.sectionSubtitle}</p>
          </div>
          <ReadOnlyTooltip readOnly={readOnly}>
            <Button
              onClick={() =>
                setFormMode({ kind: 'create', initialName: '', defaultFacilityId: scope?.facilityId ?? null })
              }
              disabled={readOnly}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              {MANUAL_BANK_COPY.addBankCta}
            </Button>
          </ReadOnlyTooltip>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : error ? (
          // Only a load that never produced data: a failed background refetch
          // keeps the cached list on screen.
          <div
            role="alert"
            className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
            {error.message}
          </div>
        ) : banks.length === 0 ? (
          // Not the DataTable's own empty state: that keeps the Columns control,
          // and an empty section offers nothing to sort or configure.
          <EmptyState
            icon={Building2}
            title={scope ? MANUAL_BANK_COPY.facilityEmptyTitle : MANUAL_BANK_COPY.emptyTitle}
            description={MANUAL_BANK_COPY.emptyDescription}
            // EmptyState has no slot props; its icon, title and text are restyled from the wrapper.
            className="[&>h3]:text-[16.5px] [&>h3]:text-slate-600 [&>p]:text-slate-400 [&>svg]:h-9 [&>svg]:w-9 [&>svg]:text-slate-300"
          />
        ) : (
          <DataTable<ManualBankInScope>
            columns={columns}
            data={banks}
            rowKey={(bank) => manualBankPath(basePath, bank)}
            onRowClick={(bank) => navigate(manualBankPath(basePath, bank))}
            sorting={{
              mode: 'client',
              sortState: sort,
              onSortChange: (next) => setSort((current) => twoWaySort(current, next)),
            }}
            itemName="financial accounts"
            showColumnControls
            enableColumnReorder
            {...columnPrefs}
          />
        )}
      </CardContent>

      {formMode && <ManualBankFormDialog mode={formMode} onClose={() => setFormMode(null)} />}
      <DeleteManualBankDialog bank={deleteTarget} onClose={() => setDeletePath(null)} />
    </Card>
  )
}
